/**
 * Auto-migration service: copies localStorage data to Supabase tables on first load.
 * Also sets up realtime subscriptions so DB changes (from server-side) appear in the UI.
 */

import { supabase } from '@/integrations/supabase/client';
import { _notifyKey } from './dataService';
import { getCurrentWorkspaceId } from './workspaceState';
import type { Contact, Conversation, Message, Campaign, CampaignRecipient, Template, SystemLog, AppNotification } from '../types';

const MIGRATION_FLAG_PREFIX = 'wf_db_migration_done_v2';

function getCollection<T>(key: string): T[] {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : [];
  } catch { return []; }
}

function getDocument<T>(key: string): T | null {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch { return null; }
}

function buildIntegrationsConfigRow(integrations: any) {
  const wsId = getCurrentWorkspaceId();
  return {
    id: wsId,
    workspace_id: wsId,
    whatsapp_config: integrations.whatsapp || {},
    ai_config: integrations.ai || {},
    ecommerce_db_config: integrations.ecommerce || {},
    webhook_config: integrations.webhook || {},
    security_config: {},
    statuses: {
      whatsapp: integrations.whatsappStatus || 'not_configured',
      ai: integrations.aiStatus || 'not_configured',
      webhook: integrations.webhookStatus || 'not_configured',
      ecommerce: integrations.ecommerceStatus || 'not_configured',
    },
    updated_at: integrations.updatedAt || new Date().toISOString(),
  };
}

export async function syncLatestIntegrationsToDb(): Promise<void> {
  const wsId = getCurrentWorkspaceId();
  const integrations = getDocument<any>(`wf_integrations:${wsId}`);
  if (!integrations) return;

  const { error } = await supabase
    .from('integrations_config' as any)
    .upsert(buildIntegrationsConfigRow(integrations) as any, { onConflict: 'id' });

  if (error) {
    console.warn('[Migration] Failed to sync integrations config:', error.message);
  }
}

/** Convert camelCase contact to snake_case DB row */
function contactToRow(c: Contact) {
  const normalizedPhone = String(c.phoneNumber || '').replace(/\D/g, '');
  return {
    id: c.id,
    full_name: c.fullName || '',
    phone_number: normalizedPhone || '',
    email: c.email || null,
    city: c.city || null,
    country: c.country || 'Morocco',
    language: c.language || 'Arabic',
    tags: c.tags || [],
    lead_status: c.leadStatus || 'new',
    assigned_agent_id: c.assignedAgentId || null,
    source: c.source || 'Manual',
    notes_count: c.notesCount || 0,
    last_contact_at: c.lastContactAt || null,
    last_message_at: c.lastMessageAt || null,
    product_name: c.productName || null,
    order_amount: c.orderAmount || null,
    order_status: c.orderStatus || null,
    delivery_status: c.deliveryStatus || null,
    cod_confirmation_status: c.codConfirmationStatus || null,
    currency: c.currency || null,
    order_created_at: c.orderCreatedAt || null,
    last_order_update_at: c.lastOrderUpdateAt || null,
    created_at: c.createdAt,
    updated_at: c.updatedAt,
  };
}

function conversationToRow(c: Conversation) {
  return {
    id: c.id,
    contact_id: c.contactId,
    channel_type: c.channelType || 'whatsapp_api',
    status: c.status || 'open',
    assigned_agent_id: c.assignedAgentId || null,
    unread_count: c.unreadCount || 0,
    tags: c.tags || [],
    last_message_preview: c.lastMessagePreview || null,
    last_message_at: c.lastMessageAt || null,
    created_at: c.createdAt,
    updated_at: c.updatedAt,
  };
}

function messageToRow(m: Message) {
  return {
    id: m.id,
    conversation_id: m.conversationId,
    contact_id: m.contactId,
    direction: m.direction,
    message_type: m.messageType || 'text',
    body: m.body || '',
    media_url: m.mediaUrl || null,
    delivery_state: m.deliveryState || 'pending',
    failure_details: m.failureDetails || null,
    external_message_id: m.externalMessageId || null,
    sent_by: m.sentBy || null,
    template_id: m.templateId || null,
    campaign_id: m.campaignId || null,
    automation_id: m.automationId || null,
    created_at: m.createdAt,
    updated_at: m.updatedAt,
  };
}

function campaignToRow(c: Campaign) {
  return {
    id: c.id,
    name: c.name || '',
    type: c.type || 'broadcast',
    status: c.status || 'draft',
    template_id: c.templateId || null,
    audience_filters: c.audienceFilters || {},
    audience_count: c.audienceCount || 0,
    scheduled_at: c.scheduledAt || null,
    started_at: c.startedAt || null,
    finished_at: c.finishedAt || null,
    total_recipients: c.totalRecipients || 0,
    sent_count: c.sentCount || 0,
    delivered_count: c.deliveredCount || 0,
    read_count: c.readCount || 0,
    failed_count: c.failedCount || 0,
    replied_count: c.repliedCount || 0,
    error_reason: c.errorReason || null,
    created_at: c.createdAt,
    updated_at: c.updatedAt,
  };
}

function templateToRow(t: Template) {
  return {
    id: t.id,
    name: t.name || '',
    category: t.category || 'UTILITY',
    language: t.language || 'en',
    header_type: t.headerType || 'none',
    header_text: t.headerText || null,
    header_media_url: t.headerMediaUrl || null,
    body_text: t.bodyText || '',
    footer_text: t.footerText || null,
    buttons: t.buttons || [],
    variables: t.variables || [],
    status: t.status || 'draft',
    preview_sample_data: t.previewSampleData || null,
    usage_count: t.usageCount || 0,
    meta_template_id: t.metaTemplateId || null,
    meta_template_name: t.metaTemplateName || null,
    rejection_reason: t.rejectionReason || null,
    quality_score: t.qualityScore || null,
    submitted_at: t.submittedAt || null,
    created_at: t.createdAt,
    updated_at: t.updatedAt,
  };
}

async function upsertBatch<T>(table: string, rows: T[], batchSize = 50) {
  for (let i = 0; i < rows.length; i += batchSize) {
    const batch = rows.slice(i, i + batchSize);
    const { error } = await supabase.from(table as any).upsert(batch as any, { onConflict: 'id' });
    if (error) {
      console.warn(`[Migration] ${table} batch ${i} error:`, error.message);
    }
  }
}

export async function migrateLocalStorageToDb(): Promise<void> {
  const migrationFlag = `${MIGRATION_FLAG_PREFIX}_${getCurrentWorkspaceId()}`;
  if (localStorage.getItem(migrationFlag)) {
    console.log('[Migration] Already migrated, skipping');
    return;
  }

  console.log('[Migration] Starting localStorage → DB migration...');

  try {
    // 1. Contacts
    const contacts = getCollection<Contact>('wf_contacts');
    if (contacts.length > 0) {
      console.log(`[Migration] Migrating ${contacts.length} contacts...`);
      await upsertBatch('contacts', contacts.map(contactToRow));
    }

    // 2. Conversations
    const conversations = getCollection<Conversation>('wf_conversations');
    if (conversations.length > 0) {
      console.log(`[Migration] Migrating ${conversations.length} conversations...`);
      await upsertBatch('conversations', conversations.map(conversationToRow));
    }

    // 3. Messages (stored per conversation)
    let totalMessages = 0;
    for (const conv of conversations) {
      const msgs = getCollection<Message>(`wf_messages_${conv.id}`);
      if (msgs.length > 0) {
        await upsertBatch('messages', msgs.map(messageToRow));
        totalMessages += msgs.length;
      }
    }
    if (totalMessages > 0) {
      console.log(`[Migration] Migrated ${totalMessages} messages`);
    }

    // 4. Campaigns
    const campaigns = getCollection<Campaign>('wf_campaigns');
    if (campaigns.length > 0) {
      console.log(`[Migration] Migrating ${campaigns.length} campaigns...`);
      await upsertBatch('campaigns', campaigns.map(campaignToRow));

      // Campaign recipients
      for (const camp of campaigns) {
        const recipients = getCollection<CampaignRecipient>(`wf_campaign_recipients_${camp.id}`);
        if (recipients.length > 0) {
          await upsertBatch('campaign_recipients', recipients.map(r => ({
            id: r.id,
            campaign_id: r.campaignId,
            contact_id: r.contactId,
            conversation_id: r.conversationId || null,
            status: r.status || 'pending',
            error_reason: r.errorReason || null,
            sent_at: r.sentAt || null,
            delivered_at: r.deliveredAt || null,
            replied_at: r.repliedAt || null,
            created_at: r.createdAt,
          })));
        }
      }
    }

    // 5. Templates
    const templates = getCollection<Template>('wf_templates');
    if (templates.length > 0) {
      console.log(`[Migration] Migrating ${templates.length} templates...`);
      await upsertBatch('templates', templates.map(templateToRow));
    }

    // 6. Logs (last 500)
    const logs = getCollection<SystemLog>('wf_logs').slice(-500);
    if (logs.length > 0) {
      console.log(`[Migration] Migrating ${logs.length} logs...`);
      await upsertBatch('system_logs', logs.map(l => ({
        id: l.id,
        module: l.module,
        action: l.action,
        entity_type: l.entityType || null,
        entity_id: l.entityId || null,
        level: l.level || 'info',
        message: l.message || '',
        metadata: l.metadata || {},
        created_at: l.createdAt,
      })));
    }

    // 7. Notifications (last 100)
    const notifications = getCollection<AppNotification>('wf_notifications').slice(-100);
    if (notifications.length > 0) {
      console.log(`[Migration] Migrating ${notifications.length} notifications...`);
      await upsertBatch('notifications', notifications.map(n => ({
        id: n.id,
        type: n.type,
        title: n.title,
        message: n.message,
        related_id: n.relatedId || null,
        module: n.module,
        is_read: n.isRead,
        created_at: n.createdAt,
      })));
    }

    // 8. AI Settings
    const aiSettings = getDocument<any>('wf_ai_settings');
    if (aiSettings) {
      await supabase.from('ai_settings' as any).upsert({
        id: getCurrentWorkspaceId(),
        workspace_id: getCurrentWorkspaceId(),
        provider: aiSettings.provider || 'lovable',
        model: aiSettings.model || 'google/gemini-2.5-flash',
        api_mode: aiSettings.apiMode || 'external',
        system_prompt: aiSettings.systemPrompt || '',
        brand_tone: aiSettings.brandTone || 'friendly',
        language_rules: aiSettings.languageRules || '',
        forbidden_words: aiSettings.forbiddenWords || [],
        product_context: aiSettings.productContext || '',
        temperature: aiSettings.temperature ?? 0.7,
        max_tokens: aiSettings.maxTokens ?? 1024,
        confidence_threshold: aiSettings.confidenceThreshold ?? 0.6,
        escalation_rules: aiSettings.escalationRules || [],
        features: aiSettings.features || {},
        updated_at: aiSettings.updatedAt || new Date().toISOString(),
      } as any, { onConflict: 'id' });
    }

    // 9. Integration config
    await syncLatestIntegrationsToDb();

    // 10. Workspace settings
    const wsSettings = getDocument<any>('wf_settings_workspace');
    if (wsSettings) {
      await supabase.from('workspace_settings' as any).upsert({
        id: getCurrentWorkspaceId(),
        workspace_id: getCurrentWorkspaceId(),
        general: wsSettings.general || {},
        preferences: wsSettings.preferences || {},
        created_at: wsSettings.createdAt || new Date().toISOString(),
        updated_at: wsSettings.updatedAt || new Date().toISOString(),
      } as any, { onConflict: 'id' });
    }

    // Mark migration as done
    localStorage.setItem(migrationFlag, new Date().toISOString());
    console.log('[Migration] ✅ localStorage → DB migration complete');
  } catch (err) {
    console.error('[Migration] Migration failed:', err);
    // Don't set flag so it retries next time
  }
}

/**
 * Convert a Supabase DB message row (snake_case) to the app's Message type (camelCase)
 * and inject it into localStorage so the UI picks it up immediately.
 */
function dbRowToMessage(row: any): Message {
  return {
    id: row.id,
    conversationId: row.conversation_id,
    contactId: row.contact_id,
    direction: row.direction,
    messageType: row.message_type || 'text',
    body: row.body || '',
    mediaUrl: row.media_url || undefined,
    deliveryState: row.delivery_state || 'pending',
    failureDetails: row.failure_details || undefined,
    externalMessageId: row.external_message_id || undefined,
    sentBy: row.sent_by || undefined,
    templateId: row.template_id || undefined,
    campaignId: row.campaign_id || undefined,
    automationId: row.automation_id || undefined,
    transcriptionText: row.transcription_text || undefined,
    transcriptionStatus: row.transcription_status || undefined,
    transcriptionError: row.transcription_error || undefined,
    transcriptionLanguage: row.transcription_language || undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function dbRowToConversation(row: any): Conversation {
  return {
    id: row.id,
    contactId: row.contact_id,
    channelType: row.channel_type || 'whatsapp_api',
    status: row.status || 'open',
    assignedAgentId: row.assigned_agent_id || undefined,
    unreadCount: row.unread_count || 0,
    tags: row.tags || [],
    lastMessagePreview: row.last_message_preview || undefined,
    lastMessageAt: row.last_message_at || undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function dbRowToContact(row: any): Contact {
  return {
    id: row.id,
    fullName: row.full_name || '',
    phoneNumber: String(row.phone_number || '').replace(/\D/g, ''),
    email: row.email || undefined,
    city: row.city || undefined,
    country: row.country || 'Morocco',
    language: row.language || 'Arabic',
    tags: row.tags || [],
    leadStatus: row.lead_status || 'new',
    assignedAgentId: row.assigned_agent_id || undefined,
    source: row.source || 'Manual',
    notesCount: row.notes_count || 0,
    lastContactAt: row.last_contact_at || undefined,
    lastMessageAt: row.last_message_at || undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    productName: row.product_name || undefined,
    orderAmount: row.order_amount || undefined,
    orderStatus: row.order_status || undefined,
    deliveryStatus: row.delivery_status || undefined,
    codConfirmationStatus: row.cod_confirmation_status || undefined,
    currency: row.currency || undefined,
    orderCreatedAt: row.order_created_at || undefined,
    lastOrderUpdateAt: row.last_order_update_at || undefined,
  };
}

/** Inject or update a message in localStorage and notify UI (with dedup by id + externalMessageId) */
function injectMessageIntoLocalStorage(msg: Message) {
  const key = `wf_messages_${msg.conversationId}`;
  const existing: Message[] = (() => {
    try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; }
  })();
  // Dedup by id
  let idx = existing.findIndex((m: Message) => m.id === msg.id);
  // Also dedup by externalMessageId (wamid) to prevent webhook + local duplication
  if (idx < 0 && msg.externalMessageId) {
    idx = existing.findIndex((m: Message) => m.externalMessageId === msg.externalMessageId);
  }
  if (idx >= 0) {
    existing[idx] = { ...existing[idx], ...msg };
  } else {
    existing.push(msg);
  }
  localStorage.setItem(key, JSON.stringify(existing));
  _notifyKey(key);
}

/** Inject or update a conversation in localStorage and notify UI */
function injectConversationIntoLocalStorage(conv: Conversation) {
  const key = 'wf_conversations';
  const existing: Conversation[] = (() => {
    try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; }
  })();
  const idx = existing.findIndex((c: Conversation) => c.id === conv.id);
  if (idx >= 0) {
    existing[idx] = { ...existing[idx], ...conv };
  } else {
    existing.push(conv);
  }
  localStorage.setItem(key, JSON.stringify(existing));
  _notifyKey(key);
}

/** Inject or update a contact in localStorage and notify UI */
function injectContactIntoLocalStorage(contact: Contact) {
  const key = 'wf_contacts';
  const existing: Contact[] = (() => {
    try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; }
  })();
  const idx = existing.findIndex((c: Contact) => c.id === contact.id);
  if (idx >= 0) {
    existing[idx] = { ...existing[idx], ...contact };
  } else {
    existing.push(contact);
  }
  localStorage.setItem(key, JSON.stringify(existing));
  _notifyKey(key);
}

/**
 * Set up realtime subscriptions so server-side changes (webhooks, automations)
 * are reflected in the UI immediately by injecting DB rows into localStorage.
 */
export function setupRealtimeSync(callbacks: {
  onContactChange?: () => void;
  onConversationChange?: () => void;
  onMessageChange?: () => void;
  onNotificationChange?: () => void;
  onLogChange?: () => void;
}) {
  const channel = supabase
    .channel('db-realtime-sync')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'contacts' }, (payload) => {
      if (payload.new && typeof payload.new === 'object' && 'id' in payload.new) {
        injectContactIntoLocalStorage(dbRowToContact(payload.new));
      }
      callbacks.onContactChange?.();
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'conversations' }, (payload) => {
      if (payload.new && typeof payload.new === 'object' && 'id' in payload.new) {
        injectConversationIntoLocalStorage(dbRowToConversation(payload.new));
      }
      callbacks.onConversationChange?.();
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'messages' }, (payload) => {
      if (payload.new && typeof payload.new === 'object' && 'id' in payload.new) {
        const msg = dbRowToMessage(payload.new);
        injectMessageIntoLocalStorage(msg);
        console.log('realtime_message_synced', { id: msg.id, direction: msg.direction, convId: msg.conversationId });
      }
      callbacks.onMessageChange?.();
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications' }, () => {
      callbacks.onNotificationChange?.();
    })
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'system_logs' }, () => {
      callbacks.onLogChange?.();
    })
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
